"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateLineItemRequestSchema = exports.LINE_ITEM_PEOPLE_MAX = exports.createLineItemRequestSchema = exports.createCollectorTaskRequestSchema = exports.setExclusionRequestSchema = exports.setPeriodOpenRequestSchema = exports.bulkContractStatusSchema = exports.updateContractRequestSchema = exports.detachContractRequestSchema = exports.attachContractRequestSchema = exports.createContractRequestSchema = exports.contractWorklogDaySchema = exports.contractWorklogSummarySchema = exports.contractWorklogSchema = exports.contractHistoryEntrySchema = exports.lineItemSchema = exports.lineItemTargetOptionSchema = exports.setLineItemTargetSchema = exports.lineItemTargetSchema = exports.contractSchema = exports.recurrenceSchema = exports.LINE_ITEM_ASSIGNMENTS = exports.LINE_ITEM_TYPES = exports.LINE_ITEM_STATUSES = exports.RECURRENCE_END_MODES = exports.RECURRENCE_UNITS = exports.CONTRACT_STATUSES = exports.CONTRACT_TYPES = void 0;
exports.lineItemHasWork = lineItemHasWork;
exports.contractHasWork = contractHasWork;
const zod_1 = require("zod");
const bulk_1 = require("../bulk");
const identifier_1 = require("../identifier");
const rules_1 = require("./rules");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C24 · Contracts and their line items. `decisions.md` P2-1 … P2-4.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A contract belongs to a project and carries line items; a task points at one line item; hours
 * reach a contract through task → line item.
 *
 * ⚠️⚠️ **THERE IS NO MONEY HERE, AND THE MOCKUP DRAWS PLENTY OF IT.** P2-1 defers the whole
 * financial layer — invoices, external costs, budgets, revenue, rates — and `docs/mockup/PHASE-2.md`
 * lists what the prototype draws with nothing behind it. `lineItemType` is the single exception, and
 * it is a type rather than an amount.
 *
 * ⚠️ **`estimateHours` and `trackedHours` are STRINGS**, like every other `numeric` in this product:
 * the driver hands `numeric` across as a string, and summing two-decimal figures somebody bills from
 * in JavaScript reintroduces float drift. Summed in SQL, rendered through `formatHours`.
 */
/** `yyyy-mm-dd`, the one wire form — see `dates.ts`. */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * 3 Nov 2026 · ⚠️ **AN END BEFORE ITS START IS REFUSED HERE, WITH THE FIELD NAMED.** The tables'
 * check constraints (`contracts_window`, the line item's window) were the only guard, and their
 * refusal reached the client as a 500. Only when BOTH ends travel — a PATCH that moves one end is
 * checked against the stored other by the constraint, which the error filter now answers as
 * `validation_failed`.
 */
function endNotBeforeStart(body, ctx) {
    if (typeof body.startsOn !== 'string' || typeof body.endsOn !== 'string')
        return;
    if (body.endsOn >= body.startsOn)
        return;
    ctx.addIssue({ code: 'custom', path: ['endsOn'], message: 'ends before it starts' });
}
exports.CONTRACT_TYPES = ['recurring', 'milestone', 'service'];
exports.CONTRACT_STATUSES = ['draft', 'active', 'closed'];
exports.RECURRENCE_UNITS = ['week', 'month'];
exports.RECURRENCE_END_MODES = ['date', 'count'];
exports.LINE_ITEM_STATUSES = ['open', 'closed'];
/**
 * ⚠️⚠️ **`license` IS C55's MUNKA NÉLKÜLI BEVÉTEL, AND IT IS THE ONLY TYPE THAT EARNS WITHOUT
 * ANYBODY LOGGING.** The mockup's own description: *"Licenc és díj: munka nélküli bevétel —
 * licenc, havidíj, viszonteladás. Nincs óra és nincs allokáció."*
 *
 * ⚠️⚠️ **A NEW MEMBER OF THIS UNION REACHES EVERY SWITCH OVER IT** — the catalogue, the line
 * item form, the finance aggregates, the effort service, the resource planner's picker and the
 * exports. Enumerate them before editing one; a `switch` with no default silently gains a hole.
 */
exports.LINE_ITEM_TYPES = ['fixed', 'time_and_material', 'non_billable', 'license'];
/**
 * ⚠️ **Does this type involve work?** `liHasWork` in the mockup, and the one predicate the rest
 * of the product should ask rather than comparing against `'license'` in fifteen places. An
 * estimate, a scope, an allocation and a task all hang off it.
 */
function lineItemHasWork(type) {
    return type !== 'license';
}
/**
 * ⚠️⚠️ **C56 · MAY WORK BE DONE AGAINST THIS CONTRACT?** The mockup's `cWorkAllowed`, which
 * is one line: *"egy projekt nélküli szerződésen csak munka nélküli sortétel lehet — feladathoz és
 * órához projekt kell."* A task belongs to a project and an hour is logged against a task, so a
 * contract with no project has nothing for either to hang from.
 *
 * ⚠️ **Which makes it the gate on the line item TYPE**: a project-less contract may carry
 * `license` items and nothing else.
 */
function contractHasWork(contract) {
    return contract.projectId !== null;
}
exports.LINE_ITEM_ASSIGNMENTS = ['manual', 'rule'];
/**
 * How a recurring contract repeats.
 *
 * ⚠️ **One object, present exactly when `type === 'recurring'`**, which mirrors the CHECK in `0050`.
 * A milestone contract carrying a half-filled recurrence is a row that says something the product
 * does not mean.
 *
 * ⚠️ **`step` is capped at 52 and `count` at 200**, which are the derivation's own limits rather
 * than arbitrary numbers: the engine walks periods one at a time, and an uncapped count is a request
 * to render an unbounded list.
 */
exports.recurrenceSchema = zod_1.z.object({
    unit: zod_1.z.enum(exports.RECURRENCE_UNITS),
    step: zod_1.z.number().int().min(1).max(52),
    startsOn: wireDateSchema,
    endMode: zod_1.z.enum(exports.RECURRENCE_END_MODES),
    /** Set when `endMode` is `date`. Null is an open-ended recurrence — the mockup's `to: ''`. */
    endsOn: wireDateSchema.nullable(),
    /** Set when `endMode` is `count`. */
    count: zod_1.z.number().int().min(1).max(200).nullable(),
});
exports.contractSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /**
     * ┌──────────────────────────────────────────────────────────────────────┐
     * │ C56 · ⚠️⚠️ `code` IS NULL WHEN THERE IS NO PROJECT, AND THAT IS THE POINT.  │
     * └──────────────────────────────────────────────────────────────────────┘
     *
     * `HSO-C01` is the PROJECT's key prefix and a per-project counter, so a contract that hangs off
     * a client has no prefix to build one from. `identifier` is what names it then, and every
     * contract has one — see `0152`.
     *
     * ⚠️ **A screen showing the code must fall back**, never print an empty cell.
     */
    code: zod_1.z.string().nullable(),
    /**
     * C56 · `cont_12a3cd5a`. Machine-made, never edited, unique across the installation — the
     * same shape and the same argument as a project's `proj_a8ed9fc8` (`0144`).
     */
    identifier: zod_1.z.string(),
    name: zod_1.z.string(),
    /**
     * ⚠️⚠️ **NULL FOR A CONTRACT THAT HANGS OFF A CLIENT.** *"A projekt opcionális"* — and a
     * project-less contract may carry only work-free line items, because a task and an hour need a
     * project to belong to. `contractHasWork` is that predicate.
     */
    projectId: zod_1.z.string().nullable(),
    projectName: zod_1.z.string().nullable(),
    projectColor: zod_1.z.string().nullable(),
    /**
     * 21 Oct 2026 · its project has no client — an internal project, whose line items are
     * non-billable only (`apps/api/src/contracts/internal-project.ts`). False with no project.
     */
    projectInternal: zod_1.z.boolean(),
    /**
     * 22 Oct 2026 · **whether this row's money may be shown** — `project.finance` for its project
     * (`project-reach.ts`). False: every amount and currency on it is null and the screen hides
     * those fields rather than drawing a blank.
     */
    moneyVisible: zod_1.z.boolean(),
    /**
     * 22 Oct 2026 · whether the caller may change it — `contract.manage`, at `own` only on a project
     * they are PM of (`project-manage.ts`). The screen offers no edit the server would refuse.
     */
    canManage: zod_1.z.boolean(),
    /** 22 Oct 2026 · whether the caller may add and change its line items — `lineitem.manage`. */
    canManageLineItems: zod_1.z.boolean(),
    /**
     * ⚠️ **Who it is with.** Derived from the project when there is one, and required only when
     * there is not — `0153` says why it is not simply *always present*: a project may itself have
     * no client, and a contract under one has none to inherit.
     */
    clientId: zod_1.z.string().nullable(),
    clientName: zod_1.z.string().nullable(),
    type: zod_1.z.enum(exports.CONTRACT_TYPES),
    status: zod_1.z.enum(exports.CONTRACT_STATUSES),
    startsOn: wireDateSchema,
    /** Null for an open-ended framework contract. */
    endsOn: wireDateSchema.nullable(),
    ownerMemberId: zod_1.z.string(),
    ownerName: zod_1.z.string(),
    ownerAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    ownerAvatarSeed: zod_1.z.string().nullable(),
    /** Present exactly when `type` is `recurring`. */
    recurrence: exports.recurrenceSchema.nullable(),
    /** How many line items hang off it. */
    /**
     * The start dates of the closed periods somebody has reopened for late entry — C29, `0056`.
     *
     * ⚠️ **On the contract rather than per period, because a period is not a row.** `occurrences()`
     * derives the list and this says which of them the calendar's *closed* has been overridden on.
     * Keyed on the date, not the index: an index is a position in a list the recurrence can change.
     */
    reopenedPeriods: zod_1.z.array(wireDateSchema),
    /**
     * C30 · the workspace's own fields, keyed by the metafield's `key`.
     *
     * ⚠️ **Empty when the workspace has defined none**, which is most of them — `{}` rather than a
     * null, so the reader never branches on "are there fields" before "are there values".
     */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    lineItemCount: zod_1.z.number().int().nonnegative(),
    /**
     * The sum of its line items' estimates.
     *
     * ⚠️ **Under a recurring contract a line item's estimate is PER PERIOD** (`0050`), so this is the
     * per-period total rather than the whole commitment. The screen says which it is; the number does
     * not carry that on its own.
     */
    estimateHours: zod_1.z.string(),
    /** Hours logged against tasks that belong to this contract's line items. */
    trackedHours: zod_1.z.string(),
    createdAt: zod_1.z.string(),
    version: zod_1.z.number().int().positive(),
});
/**
 * ⚠️⚠️ **C56 · A LINE ITEM'S PROJECT AND CODE FOLLOW ITS CONTRACT'S, SO BOTH ARE NULLABLE.**
 * A line item on a client-scoped contract has no project — which is exactly why it may only be a
 * `license`: a task needs a project and an hour needs a task. The typechecker found this by
 * refusing the tools fixture rather than anybody noticing.
 */
/**
 * `0161` · the tracker project a line item collects its tasks from, as the line item shows it.
 *
 * ⚠️ **`reachable` is the one bit a reader needs before trusting the rest**: the credential can
 * still see the project (`visible`) and the tracker has not archived it (`remote_state`). A target
 * that is not reachable syncs nothing, and the line item has to be able to say so.
 */
exports.lineItemTargetSchema = zod_1.z.object({
    instanceId: zod_1.z.string(),
    instanceName: zod_1.z.string(),
    extensionId: zod_1.z.string(),
    remoteProjectId: zod_1.z.string(),
    remoteKey: zod_1.z.string(),
    remoteName: zod_1.z.string(),
    /** P2-25's switch, re-homed (`line-item-sync.md` D7): may Forecastify write to the tracker. */
    writesEnabled: zod_1.z.boolean(),
    issueTypeId: zod_1.z.string().nullable(),
    issueTypeName: zod_1.z.string().nullable(),
    reachable: zod_1.z.boolean(),
});
/** What a request sets. Names come from the staged remote project, never from the caller. */
exports.setLineItemTargetSchema = zod_1.z.object({
    instanceId: zod_1.z.uuid(),
    remoteProjectId: zod_1.z.string().trim().min(1).max(200),
    writesEnabled: zod_1.z.boolean().optional(),
    issueTypeId: zod_1.z.string().trim().min(1).max(200).nullable().optional(),
    issueTypeName: zod_1.z.string().trim().min(1).max(200).nullable().optional(),
});
/**
 * `0161` · what a line item can target: every active integration instance, with the remote
 * projects it has staged. Names only — never a credential, never a base URL (`GET
 * /line-items/target-options`).
 */
exports.lineItemTargetOptionSchema = zod_1.z.object({
    instanceId: zod_1.z.string(),
    instanceName: zod_1.z.string(),
    extensionId: zod_1.z.string(),
    remoteProjects: zod_1.z.array(zod_1.z.object({
        remoteProjectId: zod_1.z.string(),
        key: zod_1.z.string(),
        name: zod_1.z.string(),
        /** The credential can still see it and the tracker has not archived it. */
        reachable: zod_1.z.boolean(),
    })),
});
exports.lineItemSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `li_0bb16eb3` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('lineItem'),
    contractId: zod_1.z.string(),
    /** ⚠️ C56 · null when the contract has no project; `contractIdentifier` names it then. */
    contractCode: zod_1.z.string().nullable(),
    contractIdentifier: zod_1.z.string(),
    /**
     * 3 Nov 2026 · **added under rule 8.** The contract's NAME — what every contract filter and
     * picker labels an option with (`contractOptionLabel` in the web app), the identifier riding as
     * secondary text. Without it a line item picker could only say `contract_0a1b2c3d`.
     */
    contractName: zod_1.z.string(),
    /**
     * ⚠️ U-5 / U-6 · **added under rule 8.** Whether its contract RECURS — which decides what its
     * stored money means (per period) and that it is invoiced by period, never by itself. On the
     * wire because every invoice picker has to leave such a line item out, and a picker holding line
     * items and not contracts could not otherwise tell.
     */
    contractRecurring: zod_1.z.boolean(),
    /**
     * The project the line item belongs to, read through its contract.
     *
     * ⚠️⚠️ **NULLABLE SINCE C56, AND IT USED TO SAY *"every line item has exactly one"*.**
     * `contracts.project_id` was `not null` and is not any more. A line item with no project is on
     * a client-scoped contract, which is exactly why it may only be a `license`: the two pickers
     * below need a project because a TASK needs one, and a licence has no tasks.
     *
     * ⚠️ **On the wire because two pickers need it.** A task can only be assigned to a line item on
     * its OWN project: without this field the task detail offers every line item in the workspace and
     * the line-item detail offers every task, and both are one click away from an assignment the
     * server now refuses. Derived, not stored — there is no `contract_line_items.project_id`.
     */
    projectId: zod_1.z.string().nullable(),
    /**
     * ⚠️ **Its NAME, through the same join that produces the id.** Added 14 Sep for the header
     * breadcrumb, which had an id it could navigate to and no label to print — so a line item's
     * trail skipped its project entirely and went through the contract instead.
     */
    projectName: zod_1.z.string().nullable(),
    /**
     * Its ordinal within the contract — the second half of `/contracts/HSO-C01/2` (P2-9).
     *
     * ⚠️ **Minted once and never changed**, like a task key: it survives a rename, which is the whole
     * reason the address is a number rather than the name. Migration `0053`.
     */
    number: zod_1.z.number().int().positive(),
    name: zod_1.z.string(),
    /** Per period under a recurring contract — see `contractSchema.estimateHours`. */
    estimateHours: zod_1.z.string(),
    startsOn: wireDateSchema.nullable(),
    endsOn: wireDateSchema.nullable(),
    status: zod_1.z.enum(exports.LINE_ITEM_STATUSES),
    /** ⚠️ Editable only when `contracts.line_item_pricing` is on — P2-1. */
    type: zod_1.z.enum(exports.LINE_ITEM_TYPES),
    assignment: zod_1.z.enum(exports.LINE_ITEM_ASSIGNMENTS),
    /**
     * The rule, whatever the mode.
     *
     * ⚠️ **Kept while `assignment` is `manual`**, so switching to rule mode and back does not lose
     * what somebody wrote. An empty condition list matches nothing (`rules.ts`), so a line item in
     * rule mode with nothing typed claims nothing rather than everything.
     */
    rule: rules_1.lineItemRuleSchema,
    /** `0161` · the tracker project this line item collects from. Null: it syncs nothing. */
    target: exports.lineItemTargetSchema.nullable(),
    /** How many of `taskCount` the rule brought in — the rest were named by a person. */
    ruleTaskCount: zod_1.z.number().int().nonnegative(),
    /** Tasks a rule matched and somebody threw back. */
    excludedTaskIds: zod_1.z.array(zod_1.z.string()),
    /**
     * ⚠️ **Tasks this line item's rule wants and did NOT get — P2-2.** Another line item's rule
     * matched them first, so the link went there and this is the losing claim. Recorded rather than
     * refused: a rule that silently moves which contract hours belong to is an invoice error.
     */
    overlapTaskIds: zod_1.z.array(zod_1.z.string()),
    /**
     * IP-3 · 21 Oct 2026 · **the other half of an overlap**: tasks THIS line item holds that another
     * line item's rule also wants (`line_item_overlaps`) — a closed one beside an open one, two
     * catch-alls. The targeting screen marks each with a warning triangle naming the other.
     */
    sharedWith: zod_1.z.array(zod_1.z.object({ taskId: zod_1.z.string(), lineItemId: zod_1.z.string() })),
    /**
     * ⚠️⚠️ **OS-1 · THE PEOPLE WHOSE HOURS IT COUNTS** — a time-and-material line item only. Empty is
     * the ordinary case: it counts whoever logs on its tasks. Named, it counts only theirs, inside its
     * window, and a hour by anybody else bills to no line item (`wlog_unidentified`). Ordered by name;
     * the name travels because a person who has since left the project is still named here.
     */
    people: zod_1.z.array(zod_1.z.object({ memberId: zod_1.z.string(), name: zod_1.z.string() })),
    /** C30 · the workspace's own fields — see `contractSchema.metafields`. */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    /** How many tasks point at it. */
    /**
     * ┌──────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE MONEY, ON THE ROW — round 5 item 16.                                │
     * └──────────────────────────────────────────────────────────────────┘
     *
     * *"Sortételek táblázatból hiányzik 3 oszlop: büdzsé, bevétel, feladat."* `0131` has stored all
     * three on `contract_line_items` since it was written; only the `/economics` read exposed them,
     * and that read is per OPEN line item — a table of twelve rows would have meant twelve requests.
     *
     * ⚠️⚠️ **NULL IS NOT ZERO**, and the column draws the two differently: a time-and-materials
     * deliverable HAS no budget, which is an em-dash, against an agreed budget of nothing, which is
     * `0 Ft`. `lineItemMoneySchema` records the same rule for the detail card.
     */
    /**
     * ⚠️⚠️ **U-5 · PER PERIOD ON A RECURRING CONTRACT**, the whole agreement anywhere else — the
     * stored figures, exactly as typed. A screen that shows a total multiplies by
     * `lineItemMoneyPeriods`, as it already does for the estimate (P2-11).
     *
     * ⚠️ U-7 · a licence's income is its `revenue` — the quantity, unit and unit price it was once
     * priced by are gone from the wire and nulled in the table (`0212`).
     */
    budget: zod_1.z.string().nullable(),
    revenue: zod_1.z.string().nullable(),
    /** ⚠️ What the two figures are IN. Null while neither is set — or without `moneyVisible`. */
    currencyCode: zod_1.z.string().nullable(),
    /**
     * 22 Oct 2026 · **whether this row's money may be shown** — `project.finance` for its project
     * (`project-reach.ts`). False: every amount and currency on it is null and the screen hides
     * those fields rather than drawing a blank.
     */
    moneyVisible: zod_1.z.boolean(),
    /**
     * 22 Oct 2026 · whether the caller may change it — `lineitem.manage`, at `own` only on a project
     * they are PM of (`project-manage.ts`). The screen offers no edit the server would refuse.
     */
    canManage: zod_1.z.boolean(),
    /** 22 Oct 2026 · whether the caller may change its money — `contract.manage`, as above. */
    canManageMoney: zod_1.z.boolean(),
    taskCount: zod_1.z.number().int().nonnegative(),
    trackedHours: zod_1.z.string(),
    version: zod_1.z.number().int().positive(),
});
/**
 * One row of a contract's history.
 *
 * ⚠️ **The same shape as a task's** (`taskHistoryEntrySchema`), deliberately: it is the same
 * `audit_log` row read through a different filter, and two schemas over one table is how the two
 * come to disagree about what a diff looks like.
 */
exports.contractHistoryEntrySchema = zod_1.z.object({
    id: zod_1.z.string(),
    action: zod_1.z.string(),
    /**
     * What the row is ABOUT — the contract's id, or one of its line items'.
     *
     * ⚠️ **On the wire because the line-item detail reads the CONTRACT's history.** One endpoint
     * returns both (a line item being added is part of the contract's story), so without this the
     * line item's own card showed every sibling's events — *"line item added: Riportolás és PoC"* on
     * a screen about *Havi támogatás*. Filtering by name would work until two line items share one.
     */
    resourceId: zod_1.z.string(),
    actorName: zod_1.z.string().nullable(),
    at: zod_1.z.string(),
    before: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullable(),
    after: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullable(),
});
/**
 * One worklog under a contract, flattened for the periods table.
 *
 * ⚠️⚠️ **THE PERIOD IS NOT ON THE WIRE, AND MUST NOT BE.** A period is derived (P2-3), so the
 * server sending one would freeze an answer that the recurrence definition can change a second
 * later. The client holds the same engine (`occurrences.ts`) and files each row by its `workedOn`.
 *
 * ⚠️ **`hours` is a string**, like every `numeric` in this product — see `contractSchema`.
 */
exports.contractWorklogSchema = zod_1.z.object({
    id: zod_1.z.string(),
    taskId: zod_1.z.string(),
    taskKey: zod_1.z.string(),
    taskTitle: zod_1.z.string(),
    /** Null for the contract-level collector task, which belongs to no line item of its own. */
    lineItemId: zod_1.z.string().nullable(),
    /** 31 Oct 2026 · what the period's person filter matches on — two people can share a name. */
    memberId: zod_1.z.string().nullable(),
    memberName: zod_1.z.string().nullable(),
    /** 21 Oct 2026 · the person's generated face — the normalised email, via `wireAvatarSeed`. */
    memberAvatarSeed: zod_1.z.string().nullable(),
    workedOn: wireDateSchema,
    hours: zod_1.z.string(),
});
/**
 * 31 Oct 2026 · ⚠️⚠️ **WHAT THE PERIOD'S LOG LIST NEEDS BESIDE ITS PAGE.** The list pages on the
 * server now, so the figures it showed from the rows it held come from here: the window's own count
 * and hours (the status and details cards), the filtered set's (the footer), and the people, tasks
 * and line items the window has hours on (the three filters' options — what THIS period holds, the
 * same answer the in-memory version gave).
 *
 * ⚠️ `lineItemIds` carries `null` for the collector task's hours, which belong to no line item.
 */
exports.contractWorklogSummarySchema = zod_1.z.object({
    windowCount: zod_1.z.number().int().nonnegative(),
    windowHours: zod_1.z.string(),
    count: zod_1.z.number().int().nonnegative(),
    hours: zod_1.z.string(),
    people: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string() })),
    tasks: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), key: zod_1.z.string(), title: zod_1.z.string() })),
    lineItemIds: zod_1.z.array(zod_1.z.string().nullable()),
});
/**
 * 31 Oct 2026 · one day's hours under a contract — the periods table files them into its periods.
 *
 * ⚠️ **Days, not entries**: a row per day bounds the read by the calendar rather than by how busy
 * the contract is, which is what let the table stop reading every worklog (and stopping at 5,000).
 */
exports.contractWorklogDaySchema = zod_1.z.object({
    workedOn: wireDateSchema,
    hours: zod_1.z.string(),
});
/* ── writes ──────────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **No `code` and no `status`.** The code is generated (`nextContractCode`), and a contract is
 * born `draft` — creating one already active would skip the only state that means "not agreed yet".
 */
/**
 * ⚠️⚠️ **C56 · A PROJECT *OR* A CLIENT, AND `superRefine` RATHER THAN A DISCRIMINATED
 * UNION.** The mockup's dialog has a `scope` radio, but the stored shape has no such field — see
 * `0153`. Making the REQUEST a union on a scope the row does not keep would put a fourth name for
 * the same fact on the wire; this asks the only question that matters and answers it once.
 */
exports.createContractRequestSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(1).max(200),
    /** ⚠️ Absent for a client-scoped contract. The client is then required. */
    projectId: zod_1.z.uuid().optional(),
    clientId: zod_1.z.uuid().optional(),
    type: zod_1.z.enum(exports.CONTRACT_TYPES),
    startsOn: wireDateSchema,
    endsOn: wireDateSchema.nullable().optional(),
    ownerMemberId: zod_1.z.uuid(),
    recurrence: exports.recurrenceSchema.nullable().optional(),
    /** ⚠️ Sent only when the workspace has fields — an empty object CLEARS every value. */
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
})
    .superRefine((body, ctx) => {
    if (body.projectId === undefined && body.clientId === undefined) {
        ctx.addIssue({
            code: 'custom',
            /* ⚠️ The path is `projectId` because that is the field the dialog opens on — the
               client picker only appears once somebody has chosen the other scope. */
            path: ['projectId'],
            message: 'a contract needs a project or a client',
        });
    }
    endNotBeforeStart(body, ctx);
});
/**
 * C56 · attaching an existing client-scoped contract to a project, and taking it off again.
 *
 * ⚠️⚠️ **TWO VERBS RATHER THAN A PATCH ON `projectId`, BECAUSE THEY ARE NOT SYMMETRICAL.**
 * Attaching mints a code and leaves the client alone; detaching has a GUARD — a contract whose
 * tasks and hours belong to the project it is leaving cannot go — and drops the code. A single
 * nullable patch field would make the refusal read as a validation error on a value.
 */
exports.attachContractRequestSchema = zod_1.z.object({
    projectId: zod_1.z.uuid(),
    version: zod_1.z.number().int().nonnegative(),
});
exports.detachContractRequestSchema = zod_1.z.object({
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * ⚠️ **Every field optional except `version`** — the same partial-patch shape every other resource
 * uses, so a status pick sends `{ status, version }` and nothing else. `undefined` means "leave it
 * alone" and `null` means "clear it", and the two are deliberately different.
 */
exports.updateContractRequestSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(1).max(200).optional(),
    status: zod_1.z.enum(exports.CONTRACT_STATUSES).optional(),
    startsOn: wireDateSchema.optional(),
    endsOn: wireDateSchema.nullable().optional(),
    ownerMemberId: zod_1.z.uuid().optional(),
    recurrence: exports.recurrenceSchema.nullable().optional(),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    version: zod_1.z.number().int().positive(),
})
    .superRefine(endNotBeforeStart);
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE SELECTION'S STATUS — 14 Oct 2026 feedback, the bulk of item 72.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Checkboxes provide no action except deselect all, there should be an option to change statuses
 * for all selected contracts."* `POST /contracts/bulk`, in `bulk.ts`'s one shape: the version
 * travels with every id and the answer is per row.
 *
 * ⚠️ **`status` is REQUIRED, unlike the task twin's optional fields.** Status is the only thing a
 * contract selection can be given today, and an optional one would make `{ targets }` a valid
 * request that writes nothing and audits nothing forty times.
 *
 * ⚠️ **Each row is the single `PATCH /contracts/:id` with `{ status, version }`**, so closing a
 * selection closes each contract's collector task exactly as closing one does.
 */
exports.bulkContractStatusSchema = zod_1.z.object({
    targets: bulk_1.bulkTargetsSchema,
    status: zod_1.z.enum(exports.CONTRACT_STATUSES),
});
/**
 * Which line item the collector task is named after and bills to — P2-10.
 *
 * ⚠️ **The caller chooses, and the mockup makes it a menu of the contract's open line items.** A
 * contract with three line items has no single obvious answer, and picking the first would put a
 * year of hours on whichever one happened to be created first.
 */
/** C26 · throw a rule-matched task back, or take the exclusion off. */
/**
 * C29 · reopen a closed period for late entry, or close it again.
 *
 * ⚠️ **The DATE, not the index** — see `contractSchema.reopenedPeriods` and `0056`.
 */
exports.setPeriodOpenRequestSchema = zod_1.z.object({
    startsOn: wireDateSchema,
    open: zod_1.z.boolean(),
});
exports.setExclusionRequestSchema = zod_1.z.object({ excluded: zod_1.z.boolean() });
exports.createCollectorTaskRequestSchema = zod_1.z.object({ lineItemId: zod_1.z.uuid() });
exports.createLineItemRequestSchema = zod_1.z
    .object({
    contractId: zod_1.z.uuid(),
    name: zod_1.z.string().trim().min(1).max(200),
    /** Hours, as typed. Parsed by the same helper every hour field in the product uses. */
    estimateHours: zod_1.z.number().nonnegative().max(1_000_000).optional(),
    startsOn: wireDateSchema.nullable().optional(),
    endsOn: wireDateSchema.nullable().optional(),
    type: zod_1.z.enum(exports.LINE_ITEM_TYPES).optional(),
    /**
     * ┌──────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE MONEY, AT CREATION — round 5 item 19.                              │
     * └──────────────────────────────────────────────────────────────────┘
     *
     * *"Új sortétel felvétele: kell a büdzsé/bevétel felvétele + pénznem választó."* The columns have
     * existed since `0131` and `PATCH /line-items/:id/economics` has set them since; only CREATE did
     * not, so every priced deliverable was made in two steps with a trip through a second screen.
     *
     * ⚠️⚠️ **OPTIONAL, AND ABSENT IS NOT ZERO.** A deliverable with no budget agreed is the
     * ordinary case and stays null — `lineItemMoneySchema` records why the two must stay apart.
     *
     * ⚠️ **The currency is validated as a CODE, not looked up here.** The column's composite
     * foreign key to `workspace_currencies` is what stops a workspace pricing in a currency it does
     * not work in, and a check in the schema would be a second, weaker copy of it.
     *
     * ⚠️⚠️ **U-5 · PER PERIOD ON A RECURRING CONTRACT**, as stored. A licence (U-7) is priced by
     * `revenue` alone; the unit fields it once had are gone.
     */
    budget: zod_1.z.number().min(0).optional(),
    revenue: zod_1.z.number().min(0).optional(),
    currencyCode: zod_1.z
        .string()
        .regex(/^[A-Z]{3}$/)
        .optional(),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
})
    .superRefine(endNotBeforeStart);
/**
 * ⚠️ A bound, not a rule: a placement names one person and a team a handful. It only keeps a
 * request from carrying a workspace's whole roster.
 */
exports.LINE_ITEM_PEOPLE_MAX = 50;
exports.updateLineItemRequestSchema = zod_1.z
    .object({
    /**
     * ⚠️ **Switching to `rule` re-evaluates immediately**, and switching back to `manual` leaves the
     * rule's tasks where they are rather than dropping them — see `contract.service.ts`. Neither is
     * obvious, so both are written down where the field is.
     */
    assignment: zod_1.z.enum(exports.LINE_ITEM_ASSIGNMENTS).optional(),
    rule: rules_1.lineItemRuleSchema.optional(),
    /** `0161` · a tracker project to collect tasks from, or null to stop. Absent: unchanged. */
    target: exports.setLineItemTargetSchema.nullable().optional(),
    name: zod_1.z.string().trim().min(1).max(200).optional(),
    estimateHours: zod_1.z.number().nonnegative().max(1_000_000).optional(),
    startsOn: wireDateSchema.nullable().optional(),
    endsOn: wireDateSchema.nullable().optional(),
    status: zod_1.z.enum(exports.LINE_ITEM_STATUSES).optional(),
    type: zod_1.z.enum(exports.LINE_ITEM_TYPES).optional(),
    /**
     * OS-1 · the people whose hours it counts, as the WHOLE list — `[]` names nobody again. Absent:
     * unchanged. Refused off time and material (`line_item_people_need_tm`) and for anybody who was
     * not a project member at some point inside the line item's window.
     */
    people: zod_1.z.array(zod_1.z.uuid()).max(exports.LINE_ITEM_PEOPLE_MAX).optional(),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    version: zod_1.z.number().int().positive(),
})
    .superRefine(endNotBeforeStart);
//# sourceMappingURL=contract.js.map